/** Fraction of the slot that must be visible before we count an impression. */
export const IMPRESSION_VISIBLE_RATIO = 0.5;

/**
 * Fraction of the viewport a creative too tall to ever reach `IMPRESSION_VISIBLE_RATIO`
 * must actually cover before it counts.
 */
export const IMPRESSION_VIEWPORT_RATIO = 0.9;

/**
 * IntersectionObserver may report a ratio a hair below the threshold that triggered the
 * callback (and the threshold itself is computed by division), so ratios are compared with
 * a tolerance rather than exact float math — otherwise the impression is silently skipped.
 */
const RATIO_EPSILON = 1e-6;

type Cleanup = () => void;

const noop: Cleanup = () => undefined;

/**
 * Calls `onRendered` once an ad has actually been painted into the mount point.
 *
 * "Has a child element" is NOT sufficient proof: the SDK appends its own host element
 * (`<astag-ads>`, owning a shadow root with an empty render target) on every show(), fill
 * or no fill. Counting that as a render logs an impression for every unfilled slot — the
 * exact number this instrumentation exists to audit.
 *
 * The creative renders inside that shadow root, where MutationObserver cannot see it, so a
 * non-zero height is the only trustworthy signal and ResizeObserver is what reports it.
 */
export const waitForCreative = (container: HTMLElement, onRendered: () => void): Cleanup => {
  let hasFired = false;

  const check = (): boolean => {
    if (hasFired) return false;
    if (container.childElementCount === 0) return false;
    if (container.getBoundingClientRect().height <= 0) return false;

    hasFired = true;
    onRendered();
    return true;
  };

  if (check()) return noop;

  const cleanups: Cleanup[] = [];

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => {
      if (check()) resizeObserver.disconnect();
    });
    resizeObserver.observe(container);
    cleanups.push(() => resizeObserver.disconnect());
  }

  // Backstop for environments without ResizeObserver. Only sees light-DOM changes, so it
  // cannot catch a creative painted into the shadow root — hence the height check above.
  if (typeof MutationObserver !== 'undefined') {
    const mutationObserver = new MutationObserver(() => {
      if (check()) mutationObserver.disconnect();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });
    cleanups.push(() => mutationObserver.disconnect());
  }

  return () => cleanups.forEach((cleanup) => cleanup());
};

/**
 * Fires `onImpression` the first time the rendered ad is meaningfully in view.
 *
 * Deliberately not on mount — an ad below the fold that the member never scrolls to is not
 * an impression, and counting it would inflate our numbers against AfterShip's.
 */
export const observeImpression = (container: HTMLElement, onImpression: () => void): Cleanup => {
  if (typeof IntersectionObserver === 'undefined' || typeof window === 'undefined') return noop;

  // Two rules collapsed into one ratio:
  //  - a normal creative counts once half of *it* is on screen;
  //  - one taller than the viewport can never reach that, so it counts once it covers
  //    IMPRESSION_VIEWPORT_RATIO of the *viewport* instead (intersectionRect is clipped to
  //    the viewport, so this is reachable at any height).
  // The ratio is derived from the measured height -- waitForCreative guarantees it is
  // non-zero by now -- so we can observe exactly that threshold. A fixed threshold list
  // cannot: a creative 3x the viewport peaks at ratio 0.33, so it would fire at 0.25, too
  // early to satisfy the viewport rule, and never fire again.
  const creativeHeight = container.getBoundingClientRect().height;
  const viewportFillRatio =
    creativeHeight > 0 ? (window.innerHeight * IMPRESSION_VIEWPORT_RATIO) / creativeHeight : 1;
  const requiredRatio = Math.min(IMPRESSION_VISIBLE_RATIO, viewportFillRatio);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < requiredRatio - RATIO_EPSILON) {
          return;
        }

        observer.disconnect();
        onImpression();
      });
    },
    { threshold: [0, requiredRatio] },
  );

  observer.observe(container);
  return () => observer.disconnect();
};

/**
 * Best-effort click detection.
 *
 * The SDK exposes no click callback and the creative renders across an origin boundary, so
 * a real click on it is unobservable. What we can observe is the window losing focus while
 * the pointer is over the slot, which is what happens when an ad opens in a new tab.
 *
 * This over-counts (a member who alt-tabs with the cursor parked on the ad) and
 * under-counts (keyboard activation, or a click that navigates the same tab). Treat the
 * resulting numbers as a noisy estimate with false positives and false negatives.
 */
export const observeClick = (container: HTMLElement, onClick: () => void): Cleanup => {
  if (typeof window === 'undefined') return noop;

  let pointerInside = false;

  const handlePointerEnter = () => {
    pointerInside = true;
  };
  const handlePointerLeave = () => {
    pointerInside = false;
  };
  // Catches clicks that land on our own container rather than inside the creative.
  const handleDirectClick = () => onClick();
  const handleWindowBlur = () => {
    if (pointerInside) onClick();
  };

  container.addEventListener('pointerenter', handlePointerEnter);
  container.addEventListener('pointerleave', handlePointerLeave);
  container.addEventListener('click', handleDirectClick);
  window.addEventListener('blur', handleWindowBlur);

  return () => {
    container.removeEventListener('pointerenter', handlePointerEnter);
    container.removeEventListener('pointerleave', handlePointerLeave);
    container.removeEventListener('click', handleDirectClick);
    window.removeEventListener('blur', handleWindowBlur);
  };
};
